import i18n from '../../../i18n';
import {useTranslation} from 'react-i18next';
import React, {useEffect, useState} from 'react';
import {connect} from "react-redux";
import {Link, Redirect, useHistory, useParams} from "react-router-dom";
import httpService from "../../../services/http.service";
import AppBreadcrumb from "../../Shared/AppBreadcrumb";
import {ShimmerRows} from "../../Shared/Shimmer";
import MyOpinionLogo from "../../../assets/images/MyOpinion_Logo_Head.png";
import {canManage, deleteAccount, forceLogout, resetPassword, toggleAccount} from "./actions";

const formatDate = (value) => value
    ? new Date(value).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'fr-FR')
    : null;

/** Fiche d'un compte du back-office (/moderateurs/:email, root uniquement) : identité, accès, sessions, actions. */
const ModerateurDetails = ({authUser}) => {
    const {t} = useTranslation();
    const {email} = useParams();
    const history = useHistory();

    const [admin, setAdmin] = useState(null);
    const [activity, setActivity] = useState([]);
    const [fetching, setFetching] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const {data: response} = await httpService.get(`/moderateurs/${encodeURIComponent(email)}`);
                setAdmin(response?.data);
                setActivity(response?.recent_activity || []);
            } catch (e) {
                setError(e?.response?.status === 404 ? t("Compte introuvable.") : (e?.response?.data?.message || t("Erreur de chargement.")));
            } finally {
                setFetching(false);
            }
        })();
    }, [email]);

    if (!authUser.isRoot) {
        return <Redirect to="/unauthorize" />
    }

    const act = async (fn) => {
        setBusy(true);
        const updated = await fn(admin);
        setBusy(false);
        return updated;
    };

    const onToggle = async () => { const u = await act(toggleAccount); if (u) setAdmin(u); };
    const onResetPassword = async () => { const u = await act(resetPassword); if (u) setAdmin(u); };
    const onLogout = async () => { const u = await act(forceLogout); if (u) setAdmin(u); };
    const onDelete = async () => { if (await act(deleteAccount)) history.push('/moderateurs'); };

    const manageable = admin && canManage(authUser, admin);

    const typeBadge = admin && (admin.isRoot
        ? <span className="badge badge-danger">{t("Super administrateur")}</span>
        : admin.isAdmin
            ? <span className="badge badge-warning">{t("Adminsitrateur")}</span>
            : <span className="badge badge-default">{t("Modérateur")}</span>);

    return (
        <div className="container-fluid">
            <AppBreadcrumb title={t("Détail du modérateur")}>
                <div className="col-md-6 col-sm-12 text-right">
                    <Link to="/moderateurs" className="btn btn-sm btn-light font-weight-bold">
                        <i className="fa fa-arrow-left"></i>{' '}{t("Retour à la liste")}
                    </Link>
                </div>
            </AppBreadcrumb>

            {fetching && <div className="card"><div className="body"><ShimmerRows rows={6} /></div></div>}
            {!fetching && error && <div className="alert alert-danger">{error}</div>}

            {!fetching && admin && (
                <div className="row clearfix">
                    <div className="col-lg-4 col-md-12">
                        <div className="card">
                            <div className="body text-center">
                                <img src={admin.photo || MyOpinionLogo} alt="" className="rounded-circle mb-3" style={{width: 96, height: 96, objectFit: 'cover'}} />
                                <h5 className="mb-1">{admin.nom} {admin.prenom}</h5>
                                <p className="text-muted mb-2">{admin.email}</p>
                                <div className="mb-3">
                                    {typeBadge}
                                    {' '}
                                    {admin.actif
                                        ? <span className="badge badge-success">{t("Actif")}</span>
                                        : <span className="badge badge-danger">{t("Inactif")}</span>}
                                </div>

                                {manageable ? (
                                    <div className="d-flex flex-column">
                                        <Link to={`/moderateurs?edit=${encodeURIComponent(admin.email)}`} className="btn btn-primary btn-sm mb-2">
                                            <i className="fa fa-edit"></i>{' '}{t("Modifier")}
                                        </Link>
                                        <button type="button" disabled={busy} onClick={onToggle}
                                                className={`btn btn-sm mb-2 ${admin.actif ? 'btn-warning' : 'btn-success'}`}>
                                            {admin.actif
                                                ? <><i className="fa fa-ban"></i>{' '}{t("Désactiver")}</>
                                                : <><i className="fa fa-check"></i>{' '}{t("Activer")}</>}
                                        </button>
                                        <button type="button" disabled={busy} onClick={onResetPassword} className="btn btn-secondary btn-sm mb-2">
                                            <i className="fa fa-key"></i>{' '}{t("Réinitialiser le mot de passe")}
                                        </button>
                                        <button type="button" disabled={busy || !admin.tokens_count} onClick={onLogout} className="btn btn-secondary btn-sm mb-2">
                                            <i className="fa fa-sign-out"></i>{' '}{t("Fermer les sessions ouvertes")}
                                        </button>
                                        <button type="button" disabled={busy} onClick={onDelete} className="btn btn-danger btn-sm">
                                            <i className="fa fa-trash"></i>{' '}{t("Supprimer")}
                                        </button>
                                    </div>
                                ) : (
                                    <p className="text-muted small mb-0">
                                        {authUser.id === admin.id
                                            ? t("Votre propre compte se gère depuis votre profil.")
                                            : t("Un compte root ne peut pas être modifié, désactivé ni supprimé.")}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-8 col-md-12">
                        <div className="card">
                            <div className="header"><h2>{t("Accès")}</h2></div>
                            <div className="body">
                                <table className="table table-sm mb-0">
                                    <tbody>
                                    <tr><th className="w-25">{t("Type de compte")}</th><td>{typeBadge}</td></tr>
                                    <tr>
                                        <th>{t("Rôle")}</th>
                                        <td>{admin.isRoot ? t("Tous les droits") : (admin.role_name || t("Rôle par défaut (selon le type de compte)"))}</td>
                                    </tr>
                                    <tr>
                                        <th>{t("Permissions")}</th>
                                        <td>
                                            {(admin.permissions || []).map(key => (
                                                <span key={key} className="badge badge-info mr-1 mb-1">{key}</span>
                                            ))}
                                        </td>
                                    </tr>
                                    {admin.section_type && (
                                        <tr><th>{t("Section")}</th><td>{admin.section_type} : {admin.section_name}</td></tr>
                                    )}
                                    <tr><th>{t("Dernière connexion")}</th><td>{formatDate(admin.last_login_at) || t("Jamais connecté")}</td></tr>
                                    <tr><th>{t("Sessions ouvertes")}</th><td>{admin.tokens_count || 0}</td></tr>
                                    <tr><th>{t("Créé le")}</th><td>{formatDate(admin.created_at)}</td></tr>
                                    <tr><th>{t("Modifié le")}</th><td>{formatDate(admin.updated_at)}</td></tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="card">
                            <div className="header"><h2>{t("Dernières actions")}</h2></div>
                            <div className="body">
                                {activity.length === 0 ? (
                                    <p className="text-muted mb-0">{t("Aucune action enregistrée.")}</p>
                                ) : (
                                    <table className="table table-sm mb-0">
                                        <tbody>
                                        {activity.map(item => (
                                            <tr key={item.id}>
                                                <td className="text-muted small text-nowrap">{formatDate(item.created_at)}</td>
                                                <td>{item.description}</td>
                                                <td className="text-muted small">{item.log_name}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({
    authUser: state.auth.user
});

export default connect(mapStateToProps)(ModerateurDetails);
